import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import AuthShell from './AuthShell'

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const translate = useTranslate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email) {
      setIsError(true)
      setMessage(translate('pos.auth.forgot_password.email_required'))
      return
    }

    setIsLoading(true)

    try {
      const res = await deadlineFetch('/api/users/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      })

      const data = await res.json()

      if (res.ok) {
        setIsError(false)
        setMessage(translate('pos.auth.forgot_password.success'))
      } else {
        setIsError(true)
        const errorMsg =
          data.message ||
          data.errors?.[0]?.message ||
          translate('pos.auth.forgot_password.send_failed')
        console.error('Password reset failed:', { status: res.status, message: errorMsg })
        setMessage(errorMsg)
      }
    } catch (error: any) {
      setIsError(true)
      console.error('Password reset network error:', error)
      setMessage(translate('pos.auth.forgot_password.connection_error'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthShell
      title={translate('pos.auth.forgot_password.title')}
      subtitle={translate('pos.auth.forgot_password.description')}
      footer={
        <>
          <MuiLink
            component="button"
            variant="body2"
            underline="hover"
            onClick={(e) => {
              e.preventDefault()
              window.location.href = '/vet-portal/#/login'
            }}
          >
            {translate('pos.auth.forgot_password.back_to_login')}
          </MuiLink>
          <Typography variant="body2" color="text.secondary">
            {translate('pos.login.no_account')}{' '}
            <MuiLink
              component="button"
              variant="body2"
              underline="hover"
              sx={{ verticalAlign: 'baseline' }}
              onClick={(e) => {
                e.preventDefault()
                window.location.href = '/signup'
              }}
            >
              {translate('pos.login.sign_up')}
            </MuiLink>
          </Typography>
        </>
      }
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        <TextField
          fullWidth
          label={translate('pos.auth.forgot_password.email_label')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
          sx={{ mb: 2 }}
          autoComplete="email"
        />

        {message && (
          <Alert severity={isError ? 'error' : 'success'} sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          sx={{ py: 1.25 }}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {isLoading
            ? translate('pos.auth.forgot_password.sending')
            : translate('pos.auth.forgot_password.send_button')}
        </Button>
      </Box>
    </AuthShell>
  )
}

export default ForgotPassword
